<template>
    <li :key="meter.ID" class="col-span-1 bg-white rounded-lg shadow divide-y divide-gray-200">
        <div class="w-full flex items-center justify-between p-6 space-x-6">
            <div class="flex-1 truncate">
                <div    v-if="shouldInputProbeSerial"
                        class="">
                    <floating-input     v-model:value="probeSerial"
                                        label="Enter/Scan Probe Serial"
                    ></floating-input>
                </div>
                <div    v-else
                        class="flex items-center space-x-3">
                    <span
                        class="text-gray-900 text-sm font-medium truncate">
                        Probe: {{ probeSerial === null ? 'N/A' : probeSerial }}
                    </span>
                </div>

                <div class="flex items-center space-x-3">
                    <span
                          class="text-gray-900 text-sm font-medium truncate">
                        Meter: {{ meter.SerialNumber }}
                    </span>
                </div>
                <div class="flex items-center space-x-3">
                    <span class="flex-shrink-0 inline-block px-2 py-0.5 text-blue-800 text-xs font-medium bg-blue-100 rounded-full">{{ lastReadingDate }}</span>
                </div>
                <p v-if="isValid == null"
                   class="mt-1 text-gray-500 text-sm truncate">Enter a reference value</p>
                <p v-else
                    class="mt-1 text-gray-500 text-sm truncate">{{ readingWithUnits }}</p>
            </div>
            <loading v-if="ajaxPollingData"></loading>
            <div v-else
                    class="flex flex-col items-center">
                <span   :class="isValid ? 'bg-green-300' : 'bg-red-300'"
                        class="w-10 h-10  rounded-full flex-shrink-0"></span>
                <span   v-if="meter.calibratedToday"
                        class="pt-2">Calibrated Today</span>
            </div>
        </div>
        <div>
            <div class="-mt-px flex divide-x divide-gray-200">
                <div class="w-0 flex-1 flex">
                    <span
                        v-if="! isValid"
                        class="relative -mr-px w-0 flex-1 inline-flex items-center justify-center py-4 text-sm text-gray-700 font-medium border border-transparent rounded-bl-lg">
                        <span class="ml-3">Out of Spec</span>
                    </span>
                    <template v-else
                    >
                        <span
                            v-if="!ajaxStoringCalibration && !calibratedStored"
                            @click="submitCalibration"
                            class="relative -mr-px w-0 flex-1 inline-flex items-center justify-center py-4 text-sm text-gray-700 font-medium border border-transparent rounded-bl-lg hover:text-gray-500 cursor-pointer">
                            <span   v-if="isoFacilityEnvironmentValidated"
                                    class="ml-3">Store Calibration</span>
                            <span   v-else
                                    class="ml-3">Facility Environment Status Invalid</span>
                        </span>
                        <span
                            v-if="ajaxStoringCalibration && !calibratedStored"
                            @click="submitCalibration"
                            class="relative -mr-px w-0 flex-1 inline-flex items-center justify-center py-4 text-sm text-gray-700 font-medium border border-transparent rounded-bl-lg hover:text-gray-500">
                            <loading></loading>
                        </span>
                        <span
                            v-if="calibratedStored"
                            class="bg-green-400 relative -mr-px w-0 flex-1 inline-flex items-center justify-center py-4 text-sm text-gray-700 font-medium border border-transparent rounded-bl-lg hover:text-gray-500">
                            <span class="ml-3">Saved!</span>
                        </span>
                    </template>

                </div>
                <div class="-ml-px w-0 flex-1 flex">
                    <a class="relative w-0 flex-1 inline-flex items-center justify-center py-4 text-sm text-gray-700 font-medium border border-transparent rounded-br-lg">
                        <span class="ml-3">{{errors}}</span>
                    </a>
                </div>
            </div>
        </div>
    </li>
</template>

<script>
import axios from "axios";
import Loading from "@/components/elements/loading";
import {SonicuCoreMixin} from "@/mixins/SonicuCoreMixin";

import _ from 'lodash'
import FloatingInput from "../elements/FloatingInput.vue";

export default {
    name: "ProbeCalibrationMeter",
    components: {FloatingInput, Loading},
    mixins: [SonicuCoreMixin],
    props:{
        meter: {
            type: Object,
            required: true
        },
        referenceProbe: {
            type: Object,
            required: true
        },
        calibrationEquipment: {
            type: Object,
            required: true
        },
        probeType: {
            type: String,
            required: true
        },
        ultTestingMethod: {
            type: String,
            required: true
        },
        performedByIndex: {
            type: [String, Number],
            required: true
        },
        approvedByIndex: {
            type: [String, Number],
            required: true
        },
        referenceValue: {
            type: [String, null],
            required: true
        },
        isNist: {
            type: Boolean,
            required: true
        },
        deviceSeries: {
            type: String,
            required: true
        },
        probeTypeInformation: {
            type: Object,
            required: true
        },
        isoFacilityEnvironmentValidated: {
            type: Boolean,
            required: true
        },
        bypassSSeriesBounds: {
            type: Boolean,
            required: true
        },
    },
    data(){
        return {
            isValid: null,
            lastReading: null,
            lastReadingDate: null,
            lastReadingUnits: null,
            probeSerial: '',
            ajaxStoringCalibration: false,
            ajaxPollingData: false,
            calibratedStored: false,
            calibrationErrors: null,
            referenceValueStored: null, // Store in case the prop changes but we're already valid
            errors: '',
            companyId: 'ccooper',
        };
    },
    created(){
      this.site = this.company.getCompanyId();
      if(this.environment.isLocal()){
        this.companyId = 'unittest';
      }
      this.probeSerial = (this.meter.device.probe_serial ? this.meter.device.probe_serial : '')
    },
    methods: {
        checkReferenceValue() {
            if (this.referenceValue === "") {
                this.isValid = null;
                return;
            }

            this.errors = ''
            this.ajaxPollingData = true;
            let calibrationEquipmentIds = _.map(this.calibrationEquipment, (equipment) => {
                return equipment.id;
            });
            return axios.post('/api-internal/' + this.companyId + '/probe-calibration/meter-calibration-check/' + this.meter.ID,
                {
                    'device_series': this.deviceSeries,
                    'probe_type_index': this.probeType,
                    'ult_testing_method': this.ultTestingMethod,
                    'reference_value': this.referenceValueStored,
                    'probe_serial': this.probeSerial,
                    'manually_set_probe_serial': this.shouldInputProbeSerial,
                    'bypass_s_series_bounds': this.bypassSSeriesBounds,
                    'reference_probe': this.referenceProbeIndex,
                    'equipment_ids': calibrationEquipmentIds,
                }
            ).then(response => {
                this.ajaxPollingData = false;
                if (response.data.data.hasOwnProperty('is_valid')) {
                    this.isValid = response.data.data.is_valid;
                } else {
                    console.log('did not have property');
                }
                if (response.data.data.hasOwnProperty('value')) {
                    this.lastReading = response.data.data.value;
                }
                if (response.data.data.hasOwnProperty('last_update')) {
                    this.lastReadingDate = response.data.data.last_update;
                }
                if (response.data.data.hasOwnProperty('probe_serial')) {
                    this.probeSerial = _.isNil(response.data.data.probe_serial) ? '' : response.data.data.probe_serial;
                }
                if (response.data.data.hasOwnProperty('value_units')) {
                  this.lastReadingUnits = response.data.data.value_units;
                }
            }).catch(error => {
                this.ajaxPollingData = false
                let data = error.response.data
                if (data.message) {
                    this.errors = data.message
                }
                if (data.error) {
                    this.errors = data.error
                }
            });
        },
        submitCalibration() {
            if (! this.isoFacilityEnvironmentValidated) {
                return;
            }
            this.errors = ''
            this.ajaxStoringCalibration = true;
            let calibrationEquipmentIds = _.map(this.calibrationEquipment, (equipment) => {
                return equipment.id;
            });
            return axios.post('/api-internal/probe-calibration/' + this.probeSerial,
                {
                    'device_series': this.deviceSeries,
                    'performed_by': this.performedByIndex,
                    'approved_by': this.approvedByIndex,
                    'probe_type': this.probeType,
                    'ult_testing_method': this.ultTestingMethod,
                    'reference_value': this.referenceValueStored,
                    'result': this.lastReading,
                    'reference_probe': this.referenceProbeIndex,
                    'equipment_ids': calibrationEquipmentIds,
                    'calibration_site': this.isSSeries ? 'sonicu_holding' : this.companyId,
                    'meter_id': this.meter.ID,
                    'is_nist': this.isNist,
                    'probe_serial': this.probeSerial,
                    'bypass_s_series_bounds': this.bypassSSeriesBounds,
                }
            ).then(response => {
                this.calibratedStored = true;
                this.ajaxStoringCalibration = false;
            }).catch(error => {
                this.ajaxStoringCalibration = false;
                if(error.response.data.message){
                    this.errors = error.response.data.message;
                }
            });
        }
    },
    computed: {
        readingWithUnits(){
            if (this.lastReadingUnits) {
                return this.lastReading + ' ' + this.lastReadingUnits;
            } else {
                return this.lastReading;
            }
        },
        referenceProbeIndex() {
            return _.isNil(this.referenceProbe)
                ? null
                : this.referenceProbe['index'];
        },
        isSSeries() {
            return this.deviceSeries !== 'sonishield'
        },
        shouldInputProbeSerial() {
            // Determine if the probe type is for a TS302 (Which must have its probe serial entered).
            if (_.isNil(this.probeTypeInformation)) {
                return false
            }
            return _.includes([
                    "S-Series Temperature Sensor for S-M12",
                    "S-Series Food Probe Sensor for S-M12",
                    "S-Series High-Range Temperature Sensor for S-M12"],
                this.probeTypeInformation.name)
        },
    },
    watch: {
        referenceValue(){
            if (! this.isValid) {
                this.referenceValueStored = this.referenceValue;
                this.checkReferenceValue();
            }
        },
    },
}
</script>

<style scoped>

</style>